import { DndAction, DndHeader } from "../DragInDrop.type"
import { formatValue } from "../utils"
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface TableHeaderProps {
  columns: DndHeader[]
  data: any[]
  actions: DndAction[]
  onReorder?: (newData: any[]) => void // 🟢 Nova prop para emitir a lista reordenada
  idKey?: string                       // 🟢 Chave única de cada item (Padrão: 'id')
}

// 🟢 Componente de Linha Sortable
function SortableRow({
  row,
  rowIndex,
  rowId,
  columns,
  actions,
  striped,
  hover,
  renderCellContent,
  renderActions,
}: {
  row: any
  rowIndex: number
  rowId: string | number
  columns: DndHeader[]
  actions: DndAction[]
  striped: boolean
  hover: boolean
  renderCellContent: (column: DndHeader, value: any, row: any) => React.ReactNode
  renderActions: (row: any, rowIndex: number) => React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 999 : 'auto',
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`
        ${striped && rowIndex % 2 === 1 ? 'table-striped' : ''}
        ${hover ? 'table-hover' : ''}
        ${isDragging ? 'bg-light-primary' : ''}
      `}
    >
      {/* Ícone de Grip (Arrastar) */}
      <td
        className='text-center align-middle cursor-grab'
        style={{ width: '40px', touchAction: 'none' }}
        {...attributes}
        {...listeners}
      >
        <i className='fas fa-grip-vertical text-muted fs-4'></i>
      </td>

      {columns
        .filter((col) => col.visible !== false)
        .map((column) => (
          <td
            key={column.value}
            className={`
              ${column.align === 'center' ? 'text-center' : ''}
              ${column.align === 'right' ? 'text-end' : ''}
              w-25 text-break 
            `}
            style={{ minWidth: '12rem' }}
          >
            {renderCellContent(column, row[column.value], row)}
          </td>
        ))}

      {actions && actions.length > 0 && (
        <td className='text-end'>{renderActions(row, rowIndex)}</td>
      )}
    </tr>
  )
}

export function DragInDropBody({
  data,
  columns,
  actions,
  onReorder,
  idKey = 'id',
}: TableHeaderProps) {
  const hover = true
  const striped = true

  // Função disparada ao soltar o item arrastado
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex((item) => String(item[idKey]) === String(active.id))
      const newIndex = data.findIndex((item) => String(item[idKey]) === String(over.id))

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedData = arrayMove(data, oldIndex, newIndex)
        if (onReorder) {
          onReorder(reorderedData.map((e, index) => {
            return {
              ...e,
              order: index + 1
            }
          }))
        }
      }
    }
  }

  const renderCellContent = (column: DndHeader, value: any, row: any) => {
    if (column.render) {
      return column.render(value, row)
    }

    if (column.formatter) {
      return column.formatter(value, row)
    }

    return formatValue(value, column.type)
  }

  const renderActions = (row: any, rowIndex: number) => {
    if (!actions || actions.length === 0) return null

    const visibleActions = actions.filter((action) => (action.visible ? action.visible(row) : true))

    if (visibleActions.length === 0) return null

    if (visibleActions.length <= 3) {
      return (
        <>
          {visibleActions.map((action, idx) => {
            const isDisabled = action.disabled ? action.disabled(row) : false
            return (
              <button
                key={idx}
                className={`btn p-0 border-0 bg-transparent me-2 ${action.icon && action.icon.includes('trash')
                  ? 'text-hover-danger'
                  : `${action.icon?.includes('eye') ? '' : 'text-hover-primary'}`
                  }`}
                onClick={() => action.action(row)}
                disabled={isDisabled}
                title={action.label}
                style={{ boxShadow: 'none' }}
              >
                <i
                  className={`${action.icon} fs-4 cursor-pointer ${action.hoverColor ? action.hoverColor : 'text-hover-primary'}`}
                ></i>
              </button>
            )
          })}
        </>
      )
    }

    const firstThree = visibleActions.slice(0, 3)
    const rest = visibleActions.slice(3)

    return (
      <>
        {firstThree.map((action, idx) => {
          const isDisabled = action.disabled ? action.disabled(row) : false
          return (
            <button
              key={idx}
              className='btn p-0 border-0 bg-transparent me-2'
              onClick={() => action.action(row)}
              disabled={isDisabled}
              title={action.label}
              style={{ boxShadow: 'none' }}
            >
              <i
                className={`${action.icon} fs-3 cursor-pointer ${action.hoverColor ? action.hoverColor : 'text-hover-primary'}`}
              ></i>
            </button>
          )
        })}
        <div className='dropdown d-inline'>
          <button
            className='btn btn-icon btn-sm btn-light'
            type='button'
            data-bs-toggle='dropdown'
            aria-expanded='false'
          >
            <i className='fas fa-ellipsis-v'></i>
          </button>
          <ul className='dropdown-menu'>
            {rest.map((action, actionIndex) => {
              const isDisabled = action.disabled ? action.disabled(row) : false
              return (
                <li key={actionIndex}>
                  <button
                    className={`dropdown-item ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && action.action(row)}
                    disabled={isDisabled}
                  >
                    <i className={`${action.icon} me-2`}></i>
                    {action.label}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </>
    )
  }

  if (data.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={columns.length + 2 + (actions ? 1 : 0)}
            className='text-center text-muted py-10'
          >
            <div className='d-flex flex-column align-items-center'>
              <i className='fas fa-inbox fs-2x text-muted mb-3'></i>
              <span>Nenhum registro encontrado</span>
            </div>
          </td>
        </tr>
      </tbody>
    )
  }

  // IDs ordenáveis para o SortableContext
  const itemsIds = data.map((item, index) => item[idKey] ?? index)

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemsIds} strategy={verticalListSortingStrategy}>
        <tbody>
          {data.map((row, rowIndex) => {
            const rowId = row[idKey] ?? rowIndex
            return (
              <SortableRow
                key={rowId}
                rowId={rowId}
                row={row}
                rowIndex={rowIndex}
                columns={columns}
                actions={actions}
                striped={striped}
                hover={hover}
                renderCellContent={renderCellContent}
                renderActions={renderActions}
              />
            )
          })}
        </tbody>
      </SortableContext>
    </DndContext>
  )
}